import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { RaporCekmecesi } from '../rapor-ekrani/RaporCekmecesi';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import { paraMetni, sayiMetni, tarihMetni } from '../rapor-ekrani/bicim';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { Kutu, sureHucresi } from './gorsel';
import {
  adisyonOzeti, aralikMetni, kalemSatirlari, ortalamaKalis, saatMetni, sureMetni,
  type AdisyonSatiri, type CekmeceHedefi, type HamAdisyonSatiri, type HamKalemSatiri, type KalemSatiri,
} from './tipler';

interface Props {
  /** Açık kabin / kasa ya da bölüm; `undefined` ise çekmece kapalıdır. */
  hedef: CekmeceHedefi | undefined;
  kaynak: PanelEkranKaynagi | undefined;
  kalemKaynagi: PanelEkranKaynagi | undefined;
  bas: string;
  bit: string;
  onKapat: () => void;
}

/**
 * S340: bir kabinin (ya da bir tarafın bölümünün) adisyonları, sağdan açılan çekmecede. Üstte beş özet
 * kutusu, altta yapışık "Toplam (N adisyon)". Adisyona basınca kalemleri MODALDA açılır (satır içi
 * açılır değil).
 */
export function AdisyonCekmecesi({ hedef, kaynak, kalemKaynagi, bas, bit, onKapat }: Props) {
  return (
    <RaporCekmecesi
      acik={!!hedef}
      baslik={hedef?.baslik ?? ''}
      altBaslik={`${aralikMetni(bas, bit)} · adisyona basınca kalemleri açılır`}
      onKapat={onKapat}
      testid="hamam-cekmece"
    >
      {/* `key`: başka kabine geçince açık adisyon seçimi ve önceki kabinin satırları taşınmaz. */}
      {hedef && <Icerik key={hedef.kimlik} hedef={hedef} kaynak={kaynak} kalemKaynagi={kalemKaynagi} bas={bas} bit={bit} />}
    </RaporCekmecesi>
  );
}

function Icerik({ hedef, kaynak, kalemKaynagi, bas, bit }: Omit<Props, 'hedef' | 'onKapat'> & { hedef: CekmeceHedefi }) {
  const telefon = useDarEkran();
  // `@KABIN` boşsa gönderilmez (bkz. `raporParametreleri`): bölümün tüm kabinleri gelir.
  const q = useRaporSorgu<HamAdisyonSatiri>(kaynak, {
    '@BASTARIH': bas, '@BITTARIH': bit, '@CINS': hedef.cins, '@BOLUM': hedef.bolum, '@KABIN': hedef.kabin,
  });
  const ozet = useMemo(() => adisyonOzeti(q.data ?? []), [q.data]);
  const [acik, setAcik] = useState<AdisyonSatiri | null>(null);
  const satirTiklandi = useCallback((a: AdisyonSatiri) => setAcik(a), []);

  // `kart`: telefonda adisyon kartı. Üstte tarih, sağında tutar; altında kabin, giriş, çıkış, kalem ve
  // sağında kalış; en altta nakit / kart.
  const kolonlar = useMemo<RaporKolonu<AdisyonSatiri>[]>(() => {
    const k: (RaporKolonu<AdisyonSatiri> | false)[] = [
      // Değer zaman damgasının tamamıdır (gün içi sıra doğru çıksın); hücre yalnız günü yazar.
      { id: 'tarih', baslik: 'Tarih', tur: 'tarih', deger: (a) => a.kapanis, kart: 'baslik' },
      hedef.kabinGoster && { id: 'kabin', baslik: 'Kabin', tur: 'metin', deger: (a) => a.kabin, kart: 'alt', kartEtiketi: 'Kabin' },
      { id: 'giris', baslik: 'Giriş', tur: 'metin', hizala: 'sag', genislik: 64, deger: (a) => saatMetni(a.acilis), kart: 'alt', kartEtiketi: 'Giriş' },
      { id: 'cikis', baslik: 'Çıkış', tur: 'metin', hizala: 'sag', genislik: 64, deger: (a) => saatMetni(a.kapanis), kart: 'alt', kartEtiketi: 'Çıkış' },
      {
        id: 'kalis', baslik: 'Kalış', tur: 'sayi', genislik: 96, kart: 'olcuAlt', kartEtiketi: 'Kalış',
        deger: (a) => a.kalis,
        // Kalışın toplamı toplam değildir: gösterilen adisyonların ORTALAMASIDIR.
        toplam: ortalamaKalis,
        hucre: sureHucresi,
      },
      { id: 'kalem', baslik: 'Kalem', tur: 'sayi', deger: (a) => a.kalemSayisi, toplam: true, kart: 'alt', kartEtiketi: 'Kalem' },
      // Sıfır tutar boş hücredir (nakit ödenen adisyonda kart kolonu "₺0,00" ile dolmasın).
      { id: 'nakit', baslik: 'Nakit', tur: 'para', deger: (a) => a.nakit || null, toplam: true, kart: 'ikincil' },
      { id: 'kart', baslik: 'Kart', tur: 'para', deger: (a) => a.kart || null, toplam: true, kart: 'ikincil' },
      { id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (a) => a.tutar, toplam: true, kart: 'olcu' },
    ];
    return k.filter((x): x is RaporKolonu<AdisyonSatiri> => x !== false);
  }, [hedef.kabinGoster]);

  const ilkYukleme = q.isLoading;
  const kutu = (deger: string) => (ilkYukleme ? '…' : deger);
  return (
    <>
      {/* Telefon: gövde kısalmaz, pencere tek kaydırmayla kayar; alt dolgu yok (toplam şeridi dipte). */}
      <div className={telefon ? 'flex-1 flex flex-col gap-2 px-4 pt-3 rk-kenar-16' : 'flex-1 min-h-0 flex flex-col gap-3 p-4'}>
        {!kaynak ? (
          <RaporHatasi testid="hamam-cekmece-kaynaksiz" mesaj="Bu firmada adisyon listesi kaynağı tanımlı değil." />
        ) : q.isError ? (
          <RaporHatasi testid="hamam-cekmece-hata" mesaj="Adisyonlar yüklenemedi." onTekrar={() => q.refetch()} />
        ) : (
          <>
            {/* Telefonda özet kutuları YOK: aynı sayılar alttaki toplam şeridinde yazıyor (sayı ekranda tek
                yerde, bkz. `CariHareketCekmecesi`). */}
            {!telefon && (
              <div className="grid grid-cols-5 gap-2" data-testid="hamam-cekmece-ozet" aria-busy={q.isFetching}>
                <Kutu etiket="Adisyon" kucuk>{kutu(sayiMetni(ozet.adisyonlar.length))}</Kutu>
                <Kutu etiket="Ciro" kucuk>{kutu(paraMetni(ozet.tutar))}</Kutu>
                <Kutu etiket="Nakit" kucuk>{kutu(paraMetni(ozet.nakit))}</Kutu>
                <Kutu etiket="Kart" kucuk>{kutu(paraMetni(ozet.kart))}</Kutu>
                <Kutu etiket="Ort. kalış" kucuk>{kutu(sureMetni(ozet.kalis))}</Kutu>
              </div>
            )}
            <RaporTablosu
              testid="hamam-adisyon-tablosu"
              etiket={`${hedef.baslik} adisyonları`}
              className={telefon ? '' : 'min-h-[140px]'}
              kartStili="cizgi"
              kolonlar={kolonlar}
              satirlar={ozet.adisyonlar}
              satirKimligi={(a) => a.adisyon}
              onSatirTikla={satirTiklandi}
              seciliKimlik={acik?.adisyon ?? null}
              toplamEtiketi={`Toplam (${sayiMetni(ozet.adisyonlar.length)} adisyon)`}
              bosMesaj="Bu aralıkta adisyon yok"
              yukleniyor={ilkYukleme}
              yenileniyor={q.isFetching && !ilkYukleme}
            />
          </>
        )}
      </div>
      <Modal
        open={acik !== null}
        onCancel={() => setAcik(null)}
        footer={null}
        destroyOnHidden
        width={560}
        title={
          acik && (
            <div className="font-sans" data-testid="hamam-adisyon-baslik">
              <div className="text-[15px] font-semibold text-ink-900">
                {hedef.kabinGoster && acik.kabin ? `${hedef.baslik} ${acik.kabin}` : hedef.baslik}
              </div>
              <div className="text-[13px] font-normal text-ink-500">
                {tarihMetni(acik.kapanis)} · {saatMetni(acik.acilis)} - {saatMetni(acik.kapanis)}
                {acik.kalis !== null && ` · kalış ${sureMetni(acik.kalis)}`}
              </div>
            </div>
          )
        }
      >
        {acik && <AdisyonKalemleri adisyon={acik} kaynak={kalemKaynagi} />}
      </Modal>
    </>
  );
}

/** Adisyonun kalemleri (modal gövdesi): kalem tablosu, nakit / kart ve tahsil edilen. */
function AdisyonKalemleri({ adisyon, kaynak }: { adisyon: AdisyonSatiri; kaynak: PanelEkranKaynagi | undefined }) {
  const telefon = useDarEkran();
  // Kimlik METİN gider: 19 hane sayıya çevrilseydi son haneleri yuvarlanır, başka adisyon sorulurdu.
  const q = useRaporSorgu<HamKalemSatiri>(kaynak, { '@ADISYON': adisyon.adisyon });
  const kalemler = useMemo(() => kalemSatirlari(q.data ?? []), [q.data]);
  const kalemToplami = useMemo(() => kalemler.reduce((t, k) => t + k.tutar, 0), [kalemler]);

  const kolonlar = useMemo<RaporKolonu<KalemSatiri>[]>(() => [
    { id: 'saat', baslik: 'Saat', tur: 'metin', genislik: 64, deger: (k) => k.saat, kart: 'alt' },
    { id: 'kalem', baslik: 'Kalem', tur: 'metin', deger: (k) => k.kalem, esnek: true, kart: 'baslik' },
    { id: 'adet', baslik: 'Adet', tur: 'sayi', deger: (k) => k.adet, toplam: true, kart: 'alt', kartEtiketi: 'Adet' },
    { id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (k) => k.tutar, toplam: true, kart: 'olcu' },
  ], []);

  const yuklendi = !!kaynak && q.isSuccess;
  const fark = adisyon.tutar - kalemToplami;
  return (
    <div className="flex flex-col gap-3 font-sans" data-testid="hamam-adisyon-modal">
      {!kaynak ? (
        <RaporHatasi testid="hamam-kalem-kaynaksiz" mesaj="Bu firmada adisyon kalemleri kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="hamam-kalem-hata" mesaj="Adisyon kalemleri yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        // Blok sarmalayıcı ŞART: tablo kabı `flex: 1 1 0`; doğrudan flex sütunda yüksekliği 0'a iniyordu (ölçüldü).
        <div>
        <RaporTablosu
          testid="hamam-kalem-tablosu"
          etiket="Adisyon kalemleri"
          // Masaüstünde tablo kendi içinde kayar; telefonda kart listesi doğal boyunda, modal kayar.
          className={telefon ? '' : 'max-h-[320px]'}
          kartStili="cizgi"
          kolonlar={kolonlar}
          satirlar={kalemler}
          satirKimligi={(k) => k.sira}
          toplamEtiketi="Kalem toplamı"
          bosMesaj="Bu adisyonda kalem yok"
          yukleniyor={q.isLoading}
        />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Kutu etiket="Nakit" kucuk>{paraMetni(adisyon.nakit)}</Kutu>
        <Kutu etiket="Kart" kucuk>{paraMetni(adisyon.kart)}</Kutu>
      </div>
      <Kutu
        etiket="Tahsil edilen"
        alt={yuklendi && kalemler.length > 0 && Math.abs(fark) > 0.005
          ? `Kalem toplamından farkı ${paraMetni(fark)} (adisyon indirimi)`
          : undefined}
      >
        {paraMetni(adisyon.tutar)}
      </Kutu>
    </div>
  );
}
